import { type SlotInfo, Views } from 'react-big-calendar';
import type { AppointmentResponse, ViewMode } from '@features/appointments';
import { formatPatientName } from '@utils/patientUtils';
import { format } from 'date-fns';

import type { CalendarGroupByMode } from '../../store/slice';
import { ORG_WIDE_BLOCK_RESOURCE_ID } from '../../utils/blockedTime';

import type { CalendarResource } from './types';

// Constants for valid calendar configuration values
export const VALID_STEPS = [1, 5, 10, 15] as const;
export const VALID_TIMESLOTS = [1, 2, 3, 4, 6, 12] as const;
export const DEFAULT_ENCOUNTER_DURATION = 30; // Minutes
export const UNASSIGNED_RESOURCE_ID = '__unassigned__';

// Map viewMode to react-big-calendar Views
export const getBigCalendarView = (viewMode: string) => {
  switch (viewMode) {
    case 'day':
      return Views.DAY;
    case 'week':
      return Views.WEEK;
    case 'month':
      return Views.MONTH;
    default:
      return Views.WEEK;
  }
};

/**
 * Duration (minutes) a slot selection should pre-fill on the new-appointment
 * form, or undefined when the user didn't choose one.
 *
 * Only a drag (action === 'select') is a deliberate custom duration. A plain
 * click also spans one grid step, so duration math can't tell the two apart on
 * coarse-granularity calendars — clicks return undefined so the appointment
 * type's default duration applies.
 */
export const durationFromSlotSelection = (slotInfo: SlotInfo): number | undefined => {
  if (slotInfo.action !== 'select' || !slotInfo.start || !slotInfo.end) {
    return undefined;
  }
  return Math.round((slotInfo.end.getTime() - slotInfo.start.getTime()) / (1000 * 60));
};

// Format date for display in the dialog and popover
export const formatDate = (date: Date) => {
  return format(date, 'MMMM d, yyyy h:mm a');
};

/**
 * Get display title for calendar event from appointment data
 *
 * Priority order:
 * 1. Formatted patient name (first + last + preferred)
 * 2. Self-booked requester name (quarantined prospect, pre-promotion)
 * 3. Encounter note title
 * 4. Appointment display_title
 * 5. Non-breaking space fallback
 *
 * @param appointment - Appointment response from API
 * @returns Display title string for calendar event
 */
export const getAppointmentDisplayTitle = (appointment: AppointmentResponse): string => {
  // Try patient name first (trumps everything)
  const patientName = formatPatientName(
    appointment.patient?.first_name,
    appointment.patient?.last_name,
    appointment.patient?.preferred_name,
  );

  if (patientName) {
    return patientName;
  }

  // Self-booked appointments have no patient until promotion (ADR-0077),
  // but the booker's name still belongs on the tile.
  if (appointment.block_state?.self_booked_name) {
    return appointment.block_state.self_booked_name;
  }

  // Fallback to encounter note_title
  const encounter = appointment.encounter as { note_title?: string } | null;
  if (encounter?.note_title) {
    return encounter.note_title;
  }

  // Fallback to display_title
  if (appointment.display_title) {
    return appointment.display_title;
  }

  // Final fallback: non-breaking space
  return '\u00a0';
};

/**
 * Whether the day view should split into one column per provider ("resources").
 *
 * Provider columns are an org/Practice-view feature \u2014 they only make sense when
 * viewing the whole practice. Personal ("My Schedule") view is a single provider,
 * so it must stay single-column.
 *
 * Gating on `isOrgScheduleActive` (which flips synchronously the moment the user
 * toggles scope) collapses the columns immediately, instead of waiting on the
 * async clinicians/appointments refetch that repopulates `resourceCount`. Without
 * this gate, the day view keeps rendering stale org-wide columns after switching
 * back to My Schedule (GDN-2520).
 *
 * Day view only, and only with the label lens off (`groupByMode === 'off'`);
 * week/month never use provider columns.
 */
export const shouldRenderProviderColumns = ({
  isOrgScheduleActive,
  viewMode,
  groupByMode,
  resourceCount,
}: {
  isOrgScheduleActive: boolean;
  viewMode: ViewMode;
  groupByMode: CalendarGroupByMode;
  resourceCount: number;
}): boolean =>
  isOrgScheduleActive && viewMode === 'day' && groupByMode === 'off' && resourceCount > 1;

export const getBlockedTimeResourceIds = (
  resources: CalendarResource[],
  viewMode: ViewMode,
): string[] =>
  viewMode === 'day'
    ? resources
        .map(resource => resource.id)
        .filter(id => id !== UNASSIGNED_RESOURCE_ID && id !== ORG_WIDE_BLOCK_RESOURCE_ID)
    : [];
